import { signedApiClient } from './apiClient';
import { type NetworkGroup, type NetworkGroupPermissions, StudyGroupModel } from '@/models/groups';
import type { NetworkLinkPermissions } from '@/models/studyLink';


class StudyGroupService {

  private investigationEndpoint: string = '';
  private groupEndpoint: string = '';

  initService(
    investigationEndpoint: string,
    groupEndpoint: string
  ) {
    this.investigationEndpoint = investigationEndpoint;
    this.groupEndpoint = groupEndpoint;
  }

  async getStudyGroups(identifier: string): Promise<StudyGroupModel[]> {
    const uri = `${this.investigationEndpoint}/${identifier}/groups?withUsers=true`;
    const apiClient = signedApiClient('GET', uri);
    const endpointResponse = await apiClient.get(uri);
    const networkStudyGroups: NetworkGroup[] = endpointResponse.data ? endpointResponse.data : [];
    return networkStudyGroups.map(group => StudyGroupModel.mapFromBackend(group));
  }

  async postStudyGroup(studyId: string, group: StudyGroupModel) {
    const uri = `${this.investigationEndpoint}/${studyId}/groups`;
    const apiClient = signedApiClient('POST', uri);

    const permissions: NetworkGroupPermissions = {
      viewPermissions: group.permissions.viewEntries ? 'groupEntries' : 'ownEntries',
      exportPermissions: group.permissions.export,
      manageStudyPermissions: group.permissions.manageInvest,
      editCases: true
    };
    return await apiClient.post(uri, {
      name: group.name,
      permissions,
      formsPermissions: group.permissions.forms,
      editFormsPermissions: group.permissions.editForms
    });
  }

  async patchStudyGroup(group: StudyGroupModel) {
    const uri = `${this.groupEndpoint}/${group.identifier}`;
    const apiClient = signedApiClient('PATCH', uri);
    return await apiClient.patch(uri, group);
  }

  async joinGroup(groupId: string, token?: string) {
    const uri = `${this.groupEndpoint}/${groupId}/join`;
    const apiClient = signedApiClient('POST', uri);
    const response = await apiClient.post(uri, {token});
    return response.data;
  }

  async deleteStudyGroup(group: StudyGroupModel) {
    const uri = `${this.groupEndpoint}/${group.identifier}`;
    const apiClient = signedApiClient('DELETE', uri);
    return await apiClient.delete(uri);
  }

  async activateGroupLink(id: string) {
    const uri = `${this.groupEndpoint}/${id}/link`;
    const apiClient = signedApiClient('POST', uri);

    const payload: NetworkLinkPermissions = {
      viewEntries: true,
      exportPermissions: false,
      manageInvestPermissions: false,
      forms: true,
      editForms: true,
      editCases: true
    };
    return await apiClient.post(uri, {
      permissions: payload
    });
  }

  async deactivateGroupLink(id: string) {
    const uri = `${this.groupEndpoint}/${id}/link`;
    const apiClient = signedApiClient('DELETE', uri);
    return await apiClient.delete(uri);
  }

  async resetGroupLink(id: string) {
    const uri = `${this.groupEndpoint}/${id}/link`;
    const apiClient = signedApiClient('PATCH', uri);
    return await apiClient.patch(uri);
  }

  async removeUserFromGroup(identifier: string, userIdentifier: string) {
    const uri = `${this.groupEndpoint}/${identifier}/user/${userIdentifier}`;
    const apiClient = signedApiClient('DELETE', uri);
    return await apiClient.delete(uri);
  }
}

export default new StudyGroupService();
